import { Injectable } from "@angular/core";
import { DiskService } from '../../services/disk/disk.service';
import { DeviceService } from '../../services/device/device.service';
import { AuthenticationService } from '../../services/authentication.service';
import { DB_SYNC_STATE_KEYS, DB_KEY_PREFIXES } from '../../config/pouch-db.config';
import { DeltaService, EntityNames, EntitySyncInfo } from "../delta/delta.service";
import { DynamicsClientService } from "../dynamics-client/dynamics-client.service";
import { fetchQueries } from "@omni/config/dynamics-fetchQueries";
import { DateTimeFormatsService } from '@omni/services/date-time-formats/date-time-formats.service';

@Injectable({
  providedIn: 'root'
})
export class VirtualSegmentDataService {

  constructor(
    private disk: DiskService,
    private device: DeviceService,
    private authenticationService: AuthenticationService,
    private deltaService: DeltaService,
    private readonly dynamics: DynamicsClientService,
    private readonly dateTimeFormatsService: DateTimeFormatsService,
  ) {}

  /**
   * Sync virtual segments for the user's business unit
   * @param forceFullSync - Force a full sync
   * @param loadFromDbOnly - Only load from local database
   */
  public async syncVirtualSegments(forceFullSync: boolean = false, loadFromDbOnly: boolean = false): Promise<void> {
    if (loadFromDbOnly) {
      return;
    }

    let syncState = await this.disk.getSyncState(DB_SYNC_STATE_KEYS.SYNC_VIRTUAL_SEGMENTS);
    
    // Ensure syncState exists - create if it doesn't
    if (!syncState) {
      await this.disk.createDocument(DB_SYNC_STATE_KEYS.SYNC_VIRTUAL_SEGMENTS, {
        lastUpdatedTime: null
      });
      syncState = await this.disk.getSyncState(DB_SYNC_STATE_KEYS.SYNC_VIRTUAL_SEGMENTS);
    }
    
    const isInitialSync = !syncState || !syncState.lastUpdatedTime;
    const doFullSync = forceFullSync || isInitialSync;
    const newLastUpdatedTime = new Date().getTime();

    const virtualSegmentSyncInfo: EntitySyncInfo = {
      entityName: EntityNames.virtualSegments,
      totalFailed: 0,
      totalSynced: 0,
      errors: [],
      syncStatus: true
    };

    try {
      let fetchXML = fetchQueries.virtualSegments.fetchAllVirtualSegments;
      // Access businessUnitId from user using the getter
      const businessUnitId = this.authenticationService.user.xBusinessUnitId;
      
      if (!businessUnitId) {
        console.warn('VirtualSegmentDataService: Business unit ID not found');
        virtualSegmentSyncInfo.syncStatus = false;
        this.deltaService.addEntitySyncInfo(virtualSegmentSyncInfo);
        return;
      }

      // Replace business unit ID in fetchXML
      fetchXML = fetchXML.replace('{businessUnitId}', businessUnitId);

      let response: any[] = [];
      let lastModifiedForDeltaSync: number | null = null;

      if (!doFullSync && syncState?.lastUpdatedTime) {
        lastModifiedForDeltaSync = syncState.lastUpdatedTime;
        const now = new Date();
        const hourDifference = this.dateTimeFormatsService.getDifferenceInHours(now, new Date(lastModifiedForDeltaSync)) + 1;
        
        // Add modifiedon filter for delta sync
        const deltaFilter = `<condition attribute="msdynmkt_lastupdated" operator="last-x-hours" value="${hourDifference}"/>`;
        fetchXML = fetchXML.replace('{modifiedOnFilter}', deltaFilter);
        // Delta sync - fetch all records (active and inactive) to remove inactive from local
        fetchXML = fetchXML.replace('{statusFilter}', '');
      } else {
        // Initial sync - fetch only active records (statecode = 0 means active)
        fetchXML = fetchXML.replace('{modifiedOnFilter}', '');
        const activeStatusFilter = `<condition attribute="msdynmkt_statecode" operator="eq" value="0"/>`;
        fetchXML = fetchXML.replace('{statusFilter}', activeStatusFilter);
      }

      try {
        response = await this.dynamics.executeFetchQuery('msdynmkt_virtualsegments', fetchXML);
      } catch (error) {
        console.error('VirtualSegmentDataService: syncVirtualSegments: ', error);
        this.deltaService.addSyncErrorToEntitySyncInfo(virtualSegmentSyncInfo, fetchXML, error);
        virtualSegmentSyncInfo.syncStatus = false;
        this.deltaService.addEntitySyncInfo(virtualSegmentSyncInfo);
        return;
      }

      if (response && Array.isArray(response)) {
        await this.disk.updateOrInsert(DB_KEY_PREFIXES.VIRTUAL_SEGMENTS, (doc) => {
          if (!doc || !doc.raw) {
            doc = {
              raw: []
            };
          }

          if (doFullSync) {
            // Full sync - replace all data
            doc.raw = response;
            doc.lastUpdated = newLastUpdatedTime;
          } else {
            // Delta sync - merge with existing data
            const existingMap = new Map();
            doc.raw.forEach((item: any) => {
              existingMap.set(item.msdynmkt_virtualsegmentid, item);
            });

            // Process response items
            response.forEach((item: any) => {
              const segmentId = item.msdynmkt_virtualsegmentid;
              
              // Check if record is inactive (statecode = 1 means inactive)
              if (item.msdynmkt_statecode === 1) {
                // Remove inactive records from local DB
                if (existingMap.has(segmentId)) {
                  existingMap.delete(segmentId);
                }
              } else {
                // Update or add active records
                existingMap.set(segmentId, {
                  ...item,
                  lastUpdated: newLastUpdatedTime
                });
              }
            });

            doc.raw = Array.from(existingMap.values());
            doc.lastUpdated = newLastUpdatedTime;
          }

          return doc;
        });

        virtualSegmentSyncInfo.totalSynced = response.length;
      }

      if (virtualSegmentSyncInfo.syncStatus) {
        if (!syncState) {
          syncState = {
            lastUpdatedTime: null
          };
        }
        syncState.lastUpdatedTime = newLastUpdatedTime;
        await this.disk.updateSyncState(syncState);
      }

      this.deltaService.addEntitySyncInfo(virtualSegmentSyncInfo);
    } catch (error) {
      console.error('VirtualSegmentDataService: syncVirtualSegments: ', error);
      virtualSegmentSyncInfo.syncStatus = false;
      this.deltaService.addEntitySyncInfo(virtualSegmentSyncInfo);
    }
  }

  /**
   * Get virtual segments from local database
   */
  public async getVirtualSegments(): Promise<any[]> {
    try {
      const doc = await this.disk.retrieve(DB_KEY_PREFIXES.VIRTUAL_SEGMENTS);
      return doc && doc.raw ? doc.raw : [];
    } catch (error) {
      console.error('VirtualSegmentDataService: getVirtualSegments: ', error);
      return [];
    }
  }

  /**
   * Get virtual segment by ID
   */
  public async getVirtualSegmentById(segmentId: string): Promise<any> {
    try {
      const segments = await this.getVirtualSegments();
      return segments.find(seg => seg.msdynmkt_virtualsegmentid === segmentId);
    } catch (error) {
      console.error('VirtualSegmentDataService: getVirtualSegmentById: ', error);
      return null;
    }
  }
}
